import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Group, NumberInput, ScrollArea, Select, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppAction } from "../../hooks/redux.js";
import { selectMarker, updateMarker } from "../../reducers/markerReducer.js";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { Event } from "../../constants/events.js";
import { SecondaryPanel } from "../../components/SidePanel.js";
import { ColorInput } from "../../components/ColorInput.js";
function assertValidTrigger(value) {
    if (value !== "hover" && value !== "click" && value !== "always") {
        throw new Error("Invalid trigger");
    }
}
export const MarkerConfig = ({ marker }) => {
    const intl = useIntl();
    const triggerOptions = [
        { value: "hover", label: intl.formatMessage({ id: "label.triggerHover", defaultMessage: "Hover" }) },
        { value: "click", label: intl.formatMessage({ id: "label.triggerClick", defaultMessage: "Click" }) },
        { value: "always", label: intl.formatMessage({ id: "label.triggerAlways", defaultMessage: "Always" }) },
    ];
    const captureEvent = useCaptureEvent();
    const dispatchUpdate = useAppAction(updateMarker);
    const deselect = useAppAction(selectMarker);
    const popupContent = marker.popup?.content;
    const handleClose = useCallback((event) => {
        event.stopPropagation();
        captureEvent(Event.CloseMarkerConfig);
        deselect(null);
    }, [deselect, captureEvent]);
    const handleChange = useCallback((changes) => {
        captureEvent(Event.ChangeMarkerConfig);
        dispatchUpdate(changes);
    }, [captureEvent, dispatchUpdate]);
    const handleColorChange = useCallback((color) => { handleChange({ color }); }, [handleChange]);
    const handleIconChange = useCallback((event) => { handleChange({ icon: event.target.value }); }, [handleChange]);
    const handleLngChange = useCallback((value) => {
        if (typeof value === "number") {
            handleChange({ lngLat: [value, marker.lngLat[1]] });
        }
    }, [handleChange, marker.lngLat]);
    const handleLatChange = useCallback((value) => {
        if (typeof value === "number") {
            handleChange({ lngLat: [marker.lngLat[0], value] });
        }
    }, [handleChange, marker.lngLat]);
    const handleTriggerChange = useCallback((value = "click") => {
        assertValidTrigger(value);
        handleChange({
            popup: {
                ...marker.popup,
                trigger: value,
                content: marker.popup?.content ?? {},
            },
        });
    }, [handleChange, marker.popup]);
    const handleTitleChange = useCallback((event) => {
        handleChange({
            popup: {
                ...marker.popup,
                content: {
                    ...popupContent,
                    title: event.target.value,
                },
            },
        });
    }, [handleChange, marker.popup, popupContent]);
    const handleDescriptionChange = useCallback((event) => {
        handleChange({
            popup: {
                ...marker.popup,
                content: {
                    ...popupContent,
                    description: event.target.value,
                },
            },
        });
    }, [handleChange, marker.popup, popupContent]);
    return (_jsxs(SecondaryPanel, { children: [_jsxs(Group, { p: "xs", gap: "xs", h: "50px", align: "center", justify: "space-between", children: [_jsx(Text, { fw: 500, size: "sm", truncate: true, children: popupContent?.title ?? intl.formatMessage({ id: "heading.markerConfig", defaultMessage: "Marker Config" }) }), _jsx(IconX, { onClick: handleClose })] }), _jsx(ScrollArea, { flex: "1", mih: 0, children: _jsxs(Stack, { gap: "md", children: [_jsxs(Stack, { gap: "xs", children: [_jsx(Text, { fz: "sm", fw: "bold", children: _jsx(FormattedMessage, { id: "heading.markerDetails", defaultMessage: "Marker Details" }) }), _jsx(ColorInput, { label: intl.formatMessage({ id: "label.color", defaultMessage: "Color" }), value: marker.color ?? "#3FB1CE", onChange: handleColorChange }), _jsx(TextInput, { label: intl.formatMessage({ id: "label.icon", defaultMessage: "Icon" }), size: "xs", value: marker.icon ?? "", onChange: handleIconChange }), _jsx(NumberInput, { label: intl.formatMessage({ id: "label.longitude", defaultMessage: "Longitude" }), size: "xs", value: marker.lngLat[0], onChange: handleLngChange, decimalScale: 6, step: 0.001 }), _jsx(NumberInput, { label: intl.formatMessage({ id: "label.latitude", defaultMessage: "Latitude" }), size: "xs", value: marker.lngLat[1], onChange: handleLatChange, decimalScale: 6, step: 0.001 })] }), _jsxs(Stack, { gap: "xs", children: [_jsx(Text, { fz: "sm", fw: "bold", children: _jsx(FormattedMessage, { id: "heading.popupSettings", defaultMessage: "Popup" }) }), _jsx(Select, { label: intl.formatMessage({ id: "label.trigger", defaultMessage: "Trigger" }), size: "xs", data: triggerOptions, value: marker.popup?.trigger ?? "click", onChange: handleTriggerChange }), _jsx(TextInput, { label: intl.formatMessage({ id: "label.title", defaultMessage: "Title" }), size: "xs", value: popupContent?.title ?? "", onChange: handleTitleChange }), _jsx(Textarea, { label: intl.formatMessage({ id: "label.description", defaultMessage: "Description" }), size: "xs", value: popupContent?.description ?? "", onChange: handleDescriptionChange, autosize: true, minRows: 2, maxRows: 6 })] })] }) })] }));
};
